import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocale, formatDate } from "@/i18n/format";
import { useBrandContext } from "@/contexts/brand-context";
import { usePermissions } from "@/hooks/use-permissions";
import { POST_TYPES, POST_TYPE_META } from "@/hooks/use-post-generation";
import {
  namedPromptKeys,
  promptTitle,
  requiredPlaceholders,
  usePrompts,
  usePromptVersions,
  useSavePrompt,
  useResetPromptOverride,
  useRestorePromptVersion,
  usePreviewPrompt,
  useExperiments,
  useCreateExperiment,
  useExperimentAction,
  useExperimentStats,
  type PromptOverview,
  type PromptTemplate,
  type PromptExperiment,
} from "@/hooks/use-prompts";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

export function BrandSettingsAiPromptsPage() {
  const { t } = useTranslation("settings");
  const { brand } = useBrandContext();
  const { canManageBrand } = usePermissions();
  const brandId = brand?.id ?? "";
  const { data: overview, isLoading } = usePrompts(brandId);
  const { data: experiments } = useExperiments(brandId);

  const [editing, setEditing] = useState<string | null>(null);
  const [historyFor, setHistoryFor] = useState<string | null>(null);
  const [tier, setTier] = useState<"all" | 1 | 2 | 3>("all");
  const [search, setSearch] = useState("");
  const [creatingExp, setCreatingExp] = useState(false);
  const [statsFor, setStatsFor] = useState<string | null>(null);

  const filtered = useMemo(() => {
    return POST_TYPES.filter((pt) => {
      const meta = POST_TYPE_META[pt];
      if (tier !== "all" && meta.tier !== tier) return false;
      if (search && !pt.toLowerCase().includes(search.toLowerCase()) && !meta.label.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [tier, search]);
  // Named (non-PostType) prompts have no tier; list them under "All tiers".
  const namedKeys = useMemo(() => (overview ? namedPromptKeys(overview) : []), [overview]);
  const filteredNamed = useMemo(
    () =>
      tier !== "all" || !overview
        ? []
        : namedKeys.filter((k) => {
            const label = overview[k]!.named!.label;
            return !search || `${k} ${label}`.toLowerCase().includes(search.toLowerCase());
          }),
    [overview, namedKeys, tier, search],
  );

  if (isLoading || !overview) {
    return (
      <div className="px-6 py-8 w-full space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  return (
    <div className="px-6 py-8 w-full space-y-6 max-w-6xl">
      <header>
        <h1 className="text-2xl font-semibold">{t("aiPrompts.title")}</h1>
        <p className="text-sm text-muted-foreground">
          {t("aiPrompts.subtitle")}
        </p>
      </header>

      <div className="flex items-center gap-3">
        <input
          type="text"
          placeholder={t("aiPrompts.searchPlaceholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="rounded border px-3 py-2 text-sm flex-1 max-w-xs"
        />
        <select
          value={String(tier)}
          onChange={(e) => setTier(e.target.value === "all" ? "all" : (Number(e.target.value) as 1 | 2 | 3))}
          className="rounded border px-3 py-2 text-sm"
        >
          <option value="all">{t("aiPrompts.allTiers")}</option>
          <option value="1">{t("aiPrompts.tier1")}</option>
          <option value="2">{t("aiPrompts.tier2")}</option>
          <option value="3">{t("aiPrompts.tier3")}</option>
        </select>
      </div>

      <section className="rounded-lg border divide-y">
        {filtered.map((pt) => {
          const meta = POST_TYPE_META[pt];
          const o = overview[pt];
          if (!o) return null;
          return (
            <PromptRow
              key={pt}
              postType={pt}
              title={`${meta.platform} · ${meta.label}`}
              overview={o}
              canEdit={canManageBrand}
              onEdit={() => setEditing(pt)}
              onHistory={() => setHistoryFor(pt)}
              brandId={brandId}
            />
          );
        })}
      </section>

      {filteredNamed.length > 0 && (
        <section className="space-y-2">
          <header>
            <h2 className="text-lg font-semibold">{t("aiPrompts.otherTitle")}</h2>
            <p className="text-sm text-muted-foreground">{t("aiPrompts.otherSubtitle")}</p>
          </header>
          <div className="rounded-lg border divide-y">
            {filteredNamed.map((k) => (
              <PromptRow
                key={k}
                postType={k}
                title={promptTitle(k, overview[k]!.named)}
                overview={overview[k]!}
                canEdit={canManageBrand}
                onEdit={() => setEditing(k)}
                onHistory={() => setHistoryFor(k)}
                brandId={brandId}
              />
            ))}
          </div>
        </section>
      )}

      <section className="space-y-3">
        <header className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">{t("aiPrompts.experiments")}</h2>
          {canManageBrand && (
            <Button size="sm" onClick={() => setCreatingExp(true)}>{t("aiPrompts.newExperiment")}</Button>
          )}
        </header>
        <div className="rounded-lg border divide-y">
          {(experiments ?? []).length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground">{t("aiPrompts.noExperiments")}</div>
          ) : (
            (experiments ?? []).map((e) => (
              <ExperimentRow
                key={e.id}
                experiment={e}
                brandId={brandId}
                canManage={canManageBrand}
                onShowStats={() => setStatsFor(e.id)}
              />
            ))
          )}
        </div>
      </section>

      {editing && (
        <PromptEditorModal
          brandId={brandId}
          postType={editing}
          overview={overview[editing]!}
          onClose={() => setEditing(null)}
        />
      )}
      {historyFor && (
        <VersionHistoryModal
          brandId={brandId}
          postType={historyFor}
          onClose={() => setHistoryFor(null)}
        />
      )}
      {creatingExp && (
        <CreateExperimentModal
          brandId={brandId}
          namedKeys={namedKeys}
          onClose={() => setCreatingExp(false)}
        />
      )}
      {statsFor && (
        <ExperimentStatsModal
          brandId={brandId}
          experimentId={statsFor}
          onClose={() => setStatsFor(null)}
        />
      )}
    </div>
  );
}

function PromptRow({
  postType, title, overview, canEdit, onEdit, onHistory, brandId,
}: {
  postType: string;
  title: string;
  overview: PromptOverview;
  canEdit: boolean;
  onEdit: () => void;
  onHistory: () => void;
  brandId: string;
}) {
  const { t } = useTranslation("settings");
  const reset = useResetPromptOverride(brandId, postType);
  const scope = overview.activeScope;
  const scopeBadge = {
    experiment: <span className="rounded bg-purple-500/15 text-purple-400 px-2 py-0.5 text-[10px] uppercase tracking-wider">{t("aiPrompts.scopeBadge.experiment")}</span>,
    brand: <span className="rounded bg-blue-500/15 text-blue-400 px-2 py-0.5 text-[10px] uppercase tracking-wider">{t("aiPrompts.scopeBadge.brand")}</span>,
    global: <span className="rounded bg-muted text-muted-foreground px-2 py-0.5 text-[10px] uppercase tracking-wider">{t("aiPrompts.scopeBadge.global")}</span>,
    bootstrap: <span className="rounded bg-amber-500/15 text-amber-400 px-2 py-0.5 text-[10px] uppercase tracking-wider">{t("aiPrompts.scopeBadge.bootstrap")}</span>,
  }[scope];
  return (
    <div className="flex items-center gap-3 p-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{title}</span>
          <span className="font-mono text-[11px] text-muted-foreground">{postType}</span>
          {scopeBadge}
          {overview.template && (
            <span className="text-xs text-muted-foreground">v{overview.template.version}</span>
          )}
        </div>
        {(overview.template?.notes ?? overview.named?.description) && (
          <div className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
            {overview.template?.notes ?? overview.named?.description}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {canEdit && <Button size="sm" variant="outline" onClick={onEdit}>{t("aiPrompts.edit")}</Button>}
        <Button size="sm" variant="ghost" onClick={onHistory}>{t("aiPrompts.history")}</Button>
        {canEdit && overview.override && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              if (confirm(t("aiPrompts.resetConfirm"))) {
                reset.mutate(undefined, { onSuccess: () => toast.success(t("aiPrompts.resetToGlobal")) });
              }
            }}
          >
            {t("aiPrompts.reset")}
          </Button>
        )}
      </div>
    </div>
  );
}

function PromptEditorModal({
  brandId, postType, overview, onClose,
}: {
  brandId: string;
  postType: string;
  overview: PromptOverview;
  onClose: () => void;
}) {
  const { t } = useTranslation("settings");
  const current = overview.template;
  const named = overview.named;
  // Named prompts start from their bootstrap so an edit isn't a rewrite.
  const [template, setTemplate] = useState(current?.template ?? named?.bootstrapTemplate ?? "");
  const [modelName, setModelName] = useState(current?.modelName ?? "");
  const [notes, setNotes] = useState("");
  const [previewBrief, setPreviewBrief] = useState("Sample brief: announce our new pricing tier.");
  const [previewVoice, setPreviewVoice] = useState("Bold, direct, confident.");
  const save = useSavePrompt(brandId, postType);
  const preview = usePreviewPrompt(brandId, postType);

  const missing = requiredPlaceholders(named).filter((p) => !template.includes(p));
  const hasBrief = missing.length === 0;
  const previewRan = preview.isSuccess;
  const canSave = hasBrief && previewRan && template !== current?.template;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-background border rounded-lg w-full max-w-5xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <header className="border-b p-4 flex items-center justify-between sticky top-0 bg-background">
          <div>
            <h2 className="text-lg font-semibold">{promptTitle(postType, named)}</h2>
            <p className="text-xs text-muted-foreground font-mono">{postType}</p>
          </div>
          <Button size="sm" variant="ghost" onClick={onClose}>{t("aiPrompts.editor.close")}</Button>
        </header>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 p-4">
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider mb-1">{t("aiPrompts.editor.templateLabel")}</label>
              <textarea
                value={template}
                onChange={(e) => setTemplate(e.target.value)}
                rows={20}
                className="w-full rounded border bg-background p-3 font-mono text-xs"
                placeholder={named ? "" : t("aiPrompts.editor.templatePlaceholder")}
              />
              {named && named.variables.length > 0 && (
                <p className="text-xs text-muted-foreground mt-1 font-mono">
                  {t("aiPrompts.editor.variablesHint", { vars: named.variables.map((v) => `{${v}}`).join(" ") })}
                </p>
              )}
              {!hasBrief && (
                <p className="text-xs text-red-400 mt-1">
                  {named
                    ? t("aiPrompts.editor.missingPlaceholders", { placeholders: missing.join(", ") })
                    : t("aiPrompts.editor.briefRequired")}
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium uppercase tracking-wider mb-1">{t("aiPrompts.editor.modelLabel")}</label>
                <input
                  type="text"
                  value={modelName ?? ""}
                  onChange={(e) => setModelName(e.target.value)}
                  className="w-full rounded border px-2 py-1.5 text-sm"
                  placeholder={t("aiPrompts.editor.modelPlaceholder")}
                />
              </div>
              <div>
                <label className="block text-xs font-medium uppercase tracking-wider mb-1">{t("aiPrompts.editor.notesLabel")}</label>
                <input
                  type="text"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full rounded border px-2 py-1.5 text-sm"
                  placeholder={t("aiPrompts.editor.notesPlaceholder")}
                />
              </div>
            </div>
          </div>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider mb-1">
                {named?.variables.includes("idea") ? t("aiPrompts.editor.sampleIdeaLabel") : t("aiPrompts.editor.sampleBriefLabel")}
              </label>
              <input value={previewBrief} onChange={(e) => setPreviewBrief(e.target.value)} className="w-full rounded border px-2 py-1.5 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider mb-1">{t("aiPrompts.editor.sampleVoiceLabel")}</label>
              <input value={previewVoice} onChange={(e) => setPreviewVoice(e.target.value)} className="w-full rounded border px-2 py-1.5 text-sm" />
            </div>
            <Button
              size="sm"
              disabled={!hasBrief || preview.isPending}
              onClick={() => {
                preview.mutate({
                  template,
                  modelName: modelName || undefined,
                  brief: previewBrief,
                  voice: previewVoice,
                });
              }}
            >
              {preview.isPending ? t("aiPrompts.editor.running") : t("aiPrompts.editor.runPreview")}
            </Button>
            {preview.data && (
              <div className="rounded border p-3 space-y-2">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span>{t("aiPrompts.editor.modelInfo", { model: preview.data.modelName })}</span>
                  <span>·</span>
                  <span>{preview.data.usage.inputTokens}+{preview.data.usage.outputTokens} tok</span>
                  <span>·</span>
                  <span>${preview.data.costUsd}</span>
                  <span>·</span>
                  <span>{preview.data.durationMs}ms</span>
                  <span className={preview.data.schemaOk ? "text-green-400" : "text-amber-400"}>
                    {preview.data.schemaOk ? t("aiPrompts.editor.schemaOk") : t("aiPrompts.editor.schemaInvalid")}
                  </span>
                </div>
                <pre className="text-xs bg-muted/30 rounded p-2 overflow-auto max-h-64 whitespace-pre-wrap">{preview.data.text}</pre>
                {preview.data.schemaError && <p className="text-xs text-amber-400">{preview.data.schemaError}</p>}
              </div>
            )}
            {preview.error && (
              <p className="text-xs text-red-400">{(preview.error as Error).message}</p>
            )}
          </div>
        </div>
        <footer className="border-t p-4 flex items-center justify-between sticky bottom-0 bg-background">
          <p className="text-xs text-muted-foreground">
            {previewRan ? t("aiPrompts.editor.previewRan") : t("aiPrompts.editor.previewNotRan")}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={onClose}>{t("actions.cancel", { ns: "common" })}</Button>
            <Button
              disabled={!canSave || save.isPending}
              onClick={() => {
                save.mutate(
                  { template, modelName: modelName || null, notes: notes || null },
                  {
                    onSuccess: () => {
                      toast.success(t("aiPrompts.editor.savedVersion"));
                      onClose();
                    },
                    onError: (e) => toast.error((e as Error).message),
                  },
                );
              }}
            >
              {save.isPending ? t("aiPrompts.editor.saving") : t("aiPrompts.editor.saveVersion")}
            </Button>
          </div>
        </footer>
      </div>
    </div>
  );
}

function VersionHistoryModal({
  brandId, postType, onClose,
}: { brandId: string; postType: string; onClose: () => void }) {
  const { t } = useTranslation("settings");
  const locale = useLocale();
  const { data: versions, isLoading } = usePromptVersions(brandId, postType);
  const restore = useRestorePromptVersion(brandId, postType);
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-background border rounded-lg w-full max-w-3xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <header className="border-b p-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">{t("aiPrompts.versionHistory.title", { postType })}</h2>
          <Button size="sm" variant="ghost" onClick={onClose}>{t("aiPrompts.editor.close")}</Button>
        </header>
        <div className="p-4">
          {isLoading ? <Skeleton className="h-32 w-full" /> : (
            (versions ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("aiPrompts.versionHistory.noOverrides")}</p>
            ) : (
              <ul className="space-y-2">
                {(versions ?? []).map((v: PromptTemplate) => (
                  <li key={v.id} className="rounded border p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium">v{v.version} {v.active && <span className="text-green-400">{t("aiPrompts.versionHistory.active")}</span>} {v.archivedAt && <span className="text-muted-foreground">{t("aiPrompts.versionHistory.archived")}</span>}</div>
                        <div className="text-xs text-muted-foreground">{formatDate(new Date(v.createdAt), locale, { dateStyle: "medium", timeStyle: "short" })}</div>
                        {v.notes && <div className="text-xs text-muted-foreground mt-1">{v.notes}</div>}
                      </div>
                      {!v.active && !v.archivedAt && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => restore.mutate(v.id, { onSuccess: () => toast.success(t("aiPrompts.versionHistory.restored", { version: v.version })) })}
                        >
                          {t("aiPrompts.versionHistory.restore")}
                        </Button>
                      )}
                    </div>
                    <pre className="text-xs bg-muted/30 rounded p-2 mt-2 overflow-auto max-h-40 whitespace-pre-wrap">{v.template}</pre>
                  </li>
                ))}
              </ul>
            )
          )}
        </div>
      </div>
    </div>
  );
}

function ExperimentRow({
  experiment, brandId, canManage, onShowStats,
}: { experiment: PromptExperiment; brandId: string; canManage: boolean; onShowStats: () => void }) {
  const { t } = useTranslation("settings");
  const action = useExperimentAction(brandId);
  const [concludeOpen, setConcludeOpen] = useState(false);
  return (
    <div className="p-3 flex items-center gap-3">
      <div className="flex-1">
        <div className="flex items-center gap-2 text-sm">
          <span className="font-medium">{experiment.name}</span>
          <span className="font-mono text-[11px] text-muted-foreground">{experiment.postType}</span>
          <span className={
            experiment.status === "RUNNING" ? "text-green-400 text-xs"
            : experiment.status === "PAUSED" ? "text-amber-400 text-xs"
            : experiment.status === "CONCLUDED" ? "text-muted-foreground text-xs"
            : "text-muted-foreground text-xs"
          }>{t(`aiPrompts.expStatus.${experiment.status}`)}</span>
        </div>
        <div className="text-xs text-muted-foreground mt-0.5">
          {experiment.variants.map((v) => `${v.label} ${v.weight}%`).join(" · ")}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button size="sm" variant="ghost" onClick={onShowStats}>{t("aiPrompts.expActions.stats")}</Button>
        {canManage && experiment.status === "DRAFT" && (
          <Button size="sm" onClick={() => action.mutate({ id: experiment.id, action: "start" })}>{t("aiPrompts.expActions.start")}</Button>
        )}
        {canManage && experiment.status === "RUNNING" && (
          <>
            <Button size="sm" variant="outline" onClick={() => action.mutate({ id: experiment.id, action: "pause" })}>{t("aiPrompts.expActions.pause")}</Button>
            <Button size="sm" variant="outline" onClick={() => setConcludeOpen(true)}>{t("aiPrompts.expActions.conclude")}</Button>
          </>
        )}
        {canManage && experiment.status === "PAUSED" && (
          <>
            <Button size="sm" onClick={() => action.mutate({ id: experiment.id, action: "start" })}>{t("aiPrompts.expActions.resume")}</Button>
            <Button size="sm" variant="outline" onClick={() => setConcludeOpen(true)}>{t("aiPrompts.expActions.conclude")}</Button>
          </>
        )}
      </div>
      {concludeOpen && (
        <ConcludeModal
          experiment={experiment}
          onClose={() => setConcludeOpen(false)}
          onConclude={(body) =>
            action.mutate(
              { id: experiment.id, action: "conclude", body },
              { onSuccess: () => { toast.success(t("aiPrompts.conclude.concluded")); setConcludeOpen(false); } },
            )
          }
        />
      )}
    </div>
  );
}

function ConcludeModal({
  experiment, onClose, onConclude,
}: {
  experiment: PromptExperiment;
  onClose: () => void;
  onConclude: (body: { conclusion: string; winnerTemplateId?: string }) => void;
}) {
  const { t } = useTranslation("settings");
  const [conclusion, setConclusion] = useState("");
  const [winnerTemplateId, setWinnerTemplateId] = useState<string>("");
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-background border rounded-lg w-full max-w-md p-4 space-y-3" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-semibold">{t("aiPrompts.conclude.title")}</h3>
        <label className="block text-xs font-medium uppercase tracking-wider">{t("aiPrompts.conclude.winnerLabel")}</label>
        <select value={winnerTemplateId} onChange={(e) => setWinnerTemplateId(e.target.value)} className="w-full rounded border px-2 py-1.5 text-sm">
          <option value="">{t("aiPrompts.conclude.noWinner")}</option>
          {experiment.variants.map((v) => (
            <option key={v.id} value={v.templateId}>{v.label}</option>
          ))}
        </select>
        <label className="block text-xs font-medium uppercase tracking-wider">{t("aiPrompts.conclude.conclusionLabel")}</label>
        <textarea
          value={conclusion}
          onChange={(e) => setConclusion(e.target.value)}
          rows={4}
          className="w-full rounded border p-2 text-sm"
          placeholder={t("aiPrompts.conclude.conclusionPlaceholder")}
        />
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>{t("actions.cancel", { ns: "common" })}</Button>
          <Button
            disabled={!conclusion}
            onClick={() => onConclude({ conclusion, winnerTemplateId: winnerTemplateId || undefined })}
          >
            {t("aiPrompts.conclude.conclude")}
          </Button>
        </div>
      </div>
    </div>
  );
}

function CreateExperimentModal({
  brandId, namedKeys, onClose,
}: { brandId: string; namedKeys: string[]; onClose: () => void }) {
  const { t } = useTranslation("settings");
  const [postType, setPostType] = useState<string>("IG_FEED");
  const [name, setName] = useState("");
  const { data: versions } = usePromptVersions(brandId, postType);
  const [aId, setAId] = useState("");
  const [bId, setBId] = useState("");
  const [aWeight, setAWeight] = useState(50);
  const [aLabel, setALabel] = useState("control");
  const [bLabel, setBLabel] = useState("challenger");
  const create = useCreateExperiment(brandId);
  const eligibleVersions = (versions ?? []).filter((v) => !v.archivedAt);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-background border rounded-lg w-full max-w-2xl p-4 space-y-3" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-semibold">{t("aiPrompts.createExp.title")}</h3>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium uppercase tracking-wider mb-1">{t("aiPrompts.createExp.postTypeLabel")}</label>
            <select value={postType} onChange={(e) => setPostType(e.target.value)} className="w-full rounded border px-2 py-1.5 text-sm">
              {[...POST_TYPES, ...namedKeys].map((pt) => <option key={pt} value={pt}>{pt}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium uppercase tracking-wider mb-1">{t("aiPrompts.createExp.nameLabel")}</label>
            <input value={name} onChange={(e) => setName(e.target.value)} className="w-full rounded border px-2 py-1.5 text-sm" />
          </div>
        </div>
        {eligibleVersions.length < 2 ? (
          <p className="text-sm text-amber-400">
            {t("aiPrompts.createExp.needMoreVersions", { postType })}
          </p>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-2 items-end">
              <div>
                <label className="block text-xs font-medium uppercase tracking-wider mb-1">{t("aiPrompts.createExp.aTemplate")}</label>
                <select value={aId} onChange={(e) => setAId(e.target.value)} className="w-full rounded border px-2 py-1.5 text-sm">
                  <option value="">—</option>
                  {eligibleVersions.map((v) => <option key={v.id} value={v.id}>v{v.version}{v.active ? " (active)" : ""}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium uppercase tracking-wider mb-1">{t("aiPrompts.createExp.aLabel")}</label>
                <input value={aLabel} onChange={(e) => setALabel(e.target.value)} className="w-full rounded border px-2 py-1.5 text-sm" maxLength={32} />
              </div>
              <div>
                <label className="block text-xs font-medium uppercase tracking-wider mb-1">{t("aiPrompts.createExp.aWeight")}</label>
                <input type="number" min={1} max={99} value={aWeight} onChange={(e) => setAWeight(Number(e.target.value))} className="w-full rounded border px-2 py-1.5 text-sm" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 items-end">
              <div>
                <label className="block text-xs font-medium uppercase tracking-wider mb-1">{t("aiPrompts.createExp.bTemplate")}</label>
                <select value={bId} onChange={(e) => setBId(e.target.value)} className="w-full rounded border px-2 py-1.5 text-sm">
                  <option value="">—</option>
                  {eligibleVersions.map((v) => <option key={v.id} value={v.id}>v{v.version}{v.active ? " (active)" : ""}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium uppercase tracking-wider mb-1">{t("aiPrompts.createExp.bLabel")}</label>
                <input value={bLabel} onChange={(e) => setBLabel(e.target.value)} className="w-full rounded border px-2 py-1.5 text-sm" maxLength={32} />
              </div>
              <div>
                <label className="block text-xs font-medium uppercase tracking-wider mb-1">{t("aiPrompts.createExp.bWeight")}</label>
                <input type="number" value={100 - aWeight} disabled className="w-full rounded border px-2 py-1.5 text-sm bg-muted" />
              </div>
            </div>
          </>
        )}
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>{t("actions.cancel", { ns: "common" })}</Button>
          <Button
            disabled={!name || !aId || !bId || aId === bId || create.isPending}
            onClick={() => {
              create.mutate(
                {
                  postType,
                  name,
                  variants: [
                    { templateId: aId, label: aLabel, weight: aWeight },
                    { templateId: bId, label: bLabel, weight: 100 - aWeight },
                  ],
                },
                {
                  onSuccess: () => { toast.success(t("aiPrompts.createExp.created")); onClose(); },
                  onError: (e) => toast.error((e as Error).message),
                },
              );
            }}
          >
            {t("aiPrompts.createExp.create")}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ExperimentStatsModal({
  brandId, experimentId, onClose,
}: { brandId: string; experimentId: string; onClose: () => void }) {
  const { t } = useTranslation("settings");
  const { data, isLoading } = useExperimentStats(brandId, experimentId);
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-background border rounded-lg w-full max-w-3xl p-4 space-y-3" onClick={(e) => e.stopPropagation()}>
        <header className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">{t("aiPrompts.stats.title", { name: data?.experiment.name ?? t("aiPrompts.stats.experimentFallback") })}</h3>
          <Button size="sm" variant="ghost" onClick={onClose}>{t("aiPrompts.editor.close")}</Button>
        </header>
        {isLoading ? <Skeleton className="h-32 w-full" /> : (
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground">
              <tr>
                <th className="text-left p-2">{t("aiPrompts.stats.variant")}</th>
                <th className="text-right p-2">{t("aiPrompts.stats.runs")}</th>
                <th className="text-right p-2">{t("aiPrompts.stats.errors")}</th>
                <th className="text-right p-2">{t("aiPrompts.stats.avgCost")}</th>
                <th className="text-right p-2">{t("aiPrompts.stats.avgMs")}</th>
                <th className="text-right p-2">{t("aiPrompts.stats.avgScore")}</th>
              </tr>
            </thead>
            <tbody>
              {(data?.stats ?? []).map((s) => (
                <tr key={s.variantLabel} className="border-t">
                  <td className="p-2">{s.variantLabel}</td>
                  <td className="text-right p-2">{s.runs}</td>
                  <td className="text-right p-2">{s.errors}</td>
                  <td className="text-right p-2">{s.avgCostUsd ? Number(s.avgCostUsd).toFixed(6) : "—"}</td>
                  <td className="text-right p-2">{s.avgDurationMs?.toFixed(0) ?? "—"}</td>
                  <td className="text-right p-2">{s.avgEvalScore ? Number(s.avgEvalScore).toFixed(3) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
